import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import tmdbApi from "../../api/tmdbApi";
import BankConnectChecklistModal from "../../components/stripe/BankConnectChecklistModal";

let { appname } = window.app;

// Shown right after a client finishes signup. Kicks off Stripe Connect (Express)
// onboarding so the client can link the bank account their payouts go to.
// We never collect bank details ourselves - Stripe's hosted onboarding does that,
// we only ever see back an account id + a charges/payouts-enabled status.
const ConnectBankAccount = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [connecting, setConnecting] = useState(false);
  // The checklist stands between the button and Stripe rather than after it:
  // the onboarding link is single-use, so a client who starts without their EIN
  // to hand cannot simply reopen where they left off.
  const [showChecklist, setShowChecklist] = useState(false);
  const [image, setImg] = useState(process.env.REACT_APP_IMAGE_CLOUD_FRONT);

  // Where to send the client if they skip this step, or once they come back
  // from Stripe. Passed in via navigate(..., { state: { nextRoute } }); falls
  // back to "/thankyou" (the existing post-signup landing page) otherwise.
  const nextRoute = location?.state?.nextRoute || "/thankyou";

  // Set by the plan-selection/payment flow, whose next step is always Add Store.
  // It only changes the wording ("you can still add your store, payouts wait") -
  // it is NOT a block: Add Store no longer requires a connected bank account.
  const mandatory = location?.state?.mandatory === true;

  useEffect(() => {
    if (window.site === undefined) {
      setTimeout(() => {
        if (window?.site?.common?.imageCloudfront) {
          setImg(window.site.common.imageCloudfront);
        }
      }, 1000);
    }
    if (window?.site?.common?.imageCloudfront) {
      setImg(window.site.common.imageCloudfront);
    }
  }, [window.site]);

  // This page used to adopt ?clientid=... from the URL into localStorage. That made a
  // crafted link enough to point the whole Connect flow at someone else's venue, so
  // the id now comes from the session on the backend and the URL is not read at all.

  const handleConnect = async () => {
    setConnecting(true);
    try {
      const response = await tmdbApi.createStripeConnectOnboarding();
      const result = response?.result || response;
      const onboardingUrl = result?.onboardingUrl;

      if (!onboardingUrl) {
        toast.error("Could not start bank account setup. Please try again.", { position: "bottom-center" });
        setConnecting(false);
        return;
      }

      // A full-page redirect to Stripe loses React Router state, so stash where
      // we should continue once the client is back (return/refresh pages read this).
      localStorage.setItem("stripeOnboardingNextRoute", nextRoute);
      localStorage.setItem("stripeOnboardingMandatory", mandatory ? "1" : "");
      window.location.href = onboardingUrl;
    } catch (err) {
      console.error("createStripeConnectOnboarding error:", err);
      toast.error("Something went wrong starting bank account setup.", { position: "bottom-center" });
      setConnecting(false);
    }
  };

  // "Later" continues to wherever this step was heading anyway - usually
  // "/addstore". That's safe now that Add Store no longer bounces clients back
  // here when payouts aren't enabled (see editStore.jsx); it just shows a
  // reminder, as does the Dashboard. Sending "later" to the Dashboard instead
  // was the other half of the old loop: clients who wanted to add a location
  // ended up somewhere they hadn't asked to go.
  const handleSkip = () => {
    navigate(nextRoute);
  };

  return (
    <div className="stripe-flow-page" id="page-top">
      <ToastContainer position="bottom-right" id="toastContainer" />
      <div className="container" id="connect-bank-container">
        <div className="signin-wrapper" id="signin-wrapper">
          <img
            src={`${image}${appname}/images/web/logo-dark.png`}
            className="logo"
            alt="logo"
            id="connect-bank-logo"
            style={{ cursor: "pointer" }}
            onClick={() => navigate("/dashboard")}
          />
          <div className="stripe-flow-body" id="connect-bank-section">
            <div className="stripe-flow-card" id="connect-bank-card">
              <div className="stripe-flow-card__media" id="connect-bank-icon-col">
                <span className="material-icons" aria-hidden="true">account_balance</span>
              </div>
              <div className="stripe-flow-card__content" id="connect-bank-right-column">
                <span className="stripe-flow-pill" id="connect-bank-pill">
                  <span className="stripe-flow-pill__dot" />
                  Not connected
                </span>
                <h1 className="bold-heading" id="connect-bank-heading">Connect Your Bank Account</h1>
                <p className="discription-text" id="connect-bank-text">
                  To receive payouts from bookings, set up your Stripe account and connect
                  a bank account. Stripe securely collects and stores this information -
                  we never see or store your bank details.
                </p>
                {/* The one hard requirement, named on the step that creates it.
                    Everything else about a missing bank account is a reminder;
                    this is the single place SpotSaver actually says no, so it is
                    worth stating plainly here rather than letting a client
                    discover it when a plan price refuses to save
                    (see validatePreferences in editStore.jsx). */}
                <div className="stripe-flow-callout" id="connect-bank-paid-features-note">
                  <span className="material-icons" aria-hidden="true">payments</span>
                  <p>
                    <strong>Paid plans need this.</strong> You can create your locations and
                    run free ($0) plans right away, but Plan Settings won't save a plan
                    priced above $0 until Stripe has switched your payouts on - there would
                    be nowhere to send the booking payment.
                  </p>
                </div>
                {/* Same pairing as the return page: connecting is the recommended
                    action, but skipping is a real choice - only paid plans wait on
                    it - so it gets a button, not a faint link. */}
                <div className="stripe-flow-actions" id="connect-bank-actions">
                  <button
                    className="fill-btn"
                    onClick={() => setShowChecklist(true)}
                    disabled={connecting}
                    id="connect-bank-account-btn"
                  >
                    {connecting ? "Redirecting to Stripe..." : "Connect Bank Account"}
                  </button>
                  <button
                    type="button"
                    className="stripe-flow-secondary-btn"
                    onClick={handleSkip}
                    id="connect-bank-skip-link"
                  >
                    Set up later, continue to SpotSaver
                    <span className="material-icons" aria-hidden="true">arrow_forward</span>
                  </button>
                </div>
                {mandatory && (
                  <p className="stripe-flow-note" id="connect-bank-mandatory-text">
                    You can add your first store before finishing this, but bookings can't
                    be paid out until it's done.
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <BankConnectChecklistModal
        show={showChecklist}
        onHide={() => setShowChecklist(false)}
        onContinue={handleConnect}
        connecting={connecting}
      />
      <footer className="login_footer" id="connect-bank-footer">
        <div className="container-fluid">
          <p className="footer-para" id="connect-bank-footer-text">
            © {new Date().getFullYear()} SpotSaver. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default ConnectBankAccount;
